// Auth context object + shared types, kept separate from the provider component and the
// useAuth hook so that AuthContext.tsx only exports a component (satisfies the
// react-refresh/only-export-components rule).

import { createContext } from "react";

export interface PendingNewPassword {
  // Opaque handle to the Cognito challenge; held so the Login page can complete it.
  complete: (newPassword: string) => Promise<void>;
}

export interface PendingTotp {
  // The user has a registered authenticator; submit the 6-digit code to finish sign-in.
  submit: (code: string) => Promise<void>;
}

export interface PendingTotpSetup {
  // First-time MFA enrollment: the shared secret + an otpauth URI to show as a QR code,
  // and a verify step that confirms the first generated code.
  secretCode: string;
  otpauthUri: string;
  verify: (code: string) => Promise<void>;
}

export interface AuthState {
  /** True once a valid Cognito session is held. */
  isAuthenticated: boolean;
  /** True while the initial session restore is in flight. */
  loading: boolean;
  /** The Cognito id token used as the API bearer, or null when signed out. */
  idToken: string | null;
  /** Set when sign-in needs a permanent password (invite-only first login). */
  pendingNewPassword: PendingNewPassword | null;
  /** Set when sign-in needs a TOTP code from a registered authenticator. */
  pendingTotp: PendingTotp | null;
  /** Set when the user must enroll an authenticator before sign-in can complete. */
  pendingTotpSetup: PendingTotpSetup | null;
  /** SRP sign-in. Throws on bad credentials; sets a pending challenge when required. */
  signIn: (email: string, password: string) => Promise<void>;
  /** Sign out locally and clear the session. */
  signOut: () => void;
}

export const AuthContext = createContext<AuthState | null>(null);
